import { AfterViewInit, Component, inject, ViewChild, ViewContainerRef } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { PatientDashboardStore } from 'src/app/patients/state/dashboard-page.store';
import { DayPilot, DayPilotModule, DayPilotCalendarComponent } from '@daypilot/daypilot-lite-angular';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { ERROR } from 'src/app/global';
import { Observable, takeUntil } from 'rxjs';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { AdministerMedDialogComponent } from '../administer-med-dialog/administer-med-dialog.component';


interface MedicationScheduleOrder {
  id: string;
  medId: string;

  // Used to display info in the order tooltip
  medName: string;
  route: string;
  quantityGiven: number;
  quantity: number;

  // Used by the dx-scheduler to render the order's box
  startDate: Date;
  endDate: Date;

  // Used to display the start and end times of the order within the order box
  timeToAdminister: string;

  // STAT is high, NOW is medium, and the rest are normal.
  urgency: 'normal' | 'medium' | 'high';

  // The index (0, 1, 2, 3) of which administration of this medicine this
  // is for the provider order. E.g., an order of TID has 3 administrations, morning,
  // midday, and evening. So, there are 3 of these objects. This index would be
  // 0 for morning, 1 for midday, and 2 for evening. This is used to remember
  // which scheduleOrders have been administered already so the color can be
  // set correctly (gray for already administered orders).
  schedIndex: number;

  providerOrder: ProviderOrder;

  // For insulin-type drugs (sliding scale and/or meal times)
  // which are given based on carbs and blood sugar levels,
  // we record the measured blood sugar, and the number of units administered.
  // *****
  // vtn2: what we want is to display the measured blood sugar and # of units
  // administered in the schedule. However, the objects responsible for maintaining
  // the info about each administration of a med in the schedule (a
  // MedicationScheduleOrder) are generated from info stored in a provider order
  // object, via the function getDisplayedOrders() below. So, information stored
  // in a MedicationScheduleOrder, like the blood sugar and dose given will be
  // lost each time the new objects are created... To fix this, I guess would
  // would have to store this information in the ProviderOrder... a big change.
  // So, for now (5-15-2025) we don't show that info in the schedule...
  bloodSugar: string;
  doseGiven: string;
}

// https://code.daypilot.org/28800/how-to-create-the-angular-scheduler-component-dynamically

@Component({
  selector: 'app-scheduler-wrapper',
  standalone: true,
  template: `<div #schedulerhook> </div>`,
})
export class SchedulerWrapperComponent {

  @ViewChild('schedulerhook', { read: ViewContainerRef }) schedulerhook!: ViewContainerRef;

  addScheduler() {
    this.schedulerhook.clear();
    this.schedulerhook.createComponent(MedicationScheduleComponent);
  }
}

@Component({
  selector: 'app-medication-schedule',
  templateUrl: './medication-schedule.component.html',
  styleUrls: ['./medication-schedule.component.scss'],
  providers: [DialogService],
  standalone: true,
  imports: [
    DayPilotModule,
  ],
})
export class MedicationScheduleComponent implements AfterViewInit {
  @ViewChild('scheduler') scheduler!: DayPilotCalendarComponent;

  displayedOrders: DayPilot.EventData[] = [];

  toastService = inject(ToastService);
  private patientStore = inject(PatientDashboardStore);
  private dialogService = inject(DialogService);

  patient$: Observable<Patient> = this.patientStore.patient$.pipe(
    takeUntil(this.patientStore.destroy$)
  );
  patient: Patient = new Patient();
  orders: ProviderOrder[] = [];

  configDay: DayPilot.CalendarConfig = {
    viewType: "Day",
    timeFormat: "Clock24Hours",
    businessBeginsHour: 0,
    businessEndsHour: 24,
    headerDateFormat: ' ',    // don't show any date in the header of the column
    eventMoveHandling: "Disabled",
    eventResizeHandling: "Disabled",
    timeRangeSelectedHandling: "Disabled",
    showToolTip: true,
    onBeforeEventRender: (args) => {
      const medSchedOrder = (args.data as any).appointmentData as MedicationScheduleOrder;

      // @ts-ignore
      args.data.toolTip = `${medSchedOrder.medName} \n${medSchedOrder.quantityGiven} / ${medSchedOrder.quantity} given\n${medSchedOrder.timeToAdminister}`;
      args.data.backColor = this.computeBackgroundColor(medSchedOrder);
      args.data.barColor = 'rgb(41, 91, 172)'; //'#1e40af';
      args.data.barBackColor = 'white';
      args.data.html = this.computeHtml(medSchedOrder);
    },
    onEventClick: (args) => {
      this.handleEventClick(args.e.data.appointmentData);
    },
  };

  constructor() {

    this.patient$.subscribe((patient) => {
      this.orders = patient.providerOrders.filter((order) => order.isScheduled);

      const medOrders: MedicationScheduleOrder[] = this.getDisplayedOrders();

      this.displayedOrders = medOrders.map((medSchedOrder, index) => {
        return {
          id: medSchedOrder.id,
          start: new DayPilot.Date(medSchedOrder.startDate, true),
          end: new DayPilot.Date(medSchedOrder.endDate, true),
          text: '',     // html used instead.
          appointmentData: medSchedOrder,
          // font is white unless shown against yellow (medium urgency) background.
          fontColor: medSchedOrder.urgency === 'medium' ? 'black' : 'white',
          // html: `
          //   <div class="medication-name">
          //     <strong>${medSchedOrder.medName}</strong>
          //   </div>
          //   <div class="time-range">
          //     ${medSchedOrder.timeToAdminister}
          //   </div>
          // `,
          // Following only works if you have DayPilot Pro.
          // bubbleHtml: `
          //   <div> <strong>${order.medName}</strong> </div>
          //   <div> Route: ${order.route} </div>
          //   <div>
          //     ${order.quantityGiven} / ${order.quantity} given
          //   </div>
          //   <div>
          //     ${order.timeToAdminister}
          //   </div>
          // `,
          toolTip: `
            <div> <strong>${medSchedOrder.medName}</strong> </div>
            <div> Route: ${medSchedOrder.route} </div>
            <div>
              ${medSchedOrder.quantityGiven} / ${medSchedOrder.quantity} given
            </div>
            <div>
              ${medSchedOrder.timeToAdminister}
            </div>
          `,
        };
      });
    });
  }

  ngAfterViewInit(): void {
    this.loadEvents();
  }

  loadEvents() {
    this.scheduler.control.events.list = this.displayedOrders;
    this.scheduler.control.update();
  }

  /**
   * Converts ProviderOrder objects into objects that can be passed into the DayPilot Scheduler.
   */
  getDisplayedOrders(): MedicationScheduleOrder[] {
    // items below are arrays of orders, which should be flattened afterward.
    return this.orders.flatMap((order: ProviderOrder) => {
      return this.generateMedicationSchedule(order);
    });
  }

  private orderTypeToUrgency(order: ProviderOrder): "normal" | "medium" | "high" {
    return order.isSTAT ? 'high' : order.isNOW ? 'medium' : 'normal';
  }

  // Helper function to get the dose name based on hour
  private getDoseName(hour: number): string {
    if (hour < 12) return 'morning';
    if (hour < 17) return 'midday';
    if (hour < 22) return 'evening';
    return 'night';
  }

  // Main function to generate medication schedule
  private generateMedicationSchedule(order: ProviderOrder): MedicationScheduleOrder | MedicationScheduleOrder[] {

    const scheduleTimes = order.providerOrder2MedSchedule();
    return order.providerOrder2MedAdminWindows().map((sched, idx) => {

      const startDate = new Date();
      startDate.setHours(sched.hour, sched.minute, 0);
      const endDate = new Date(startDate);
      endDate.setTime(startDate.getTime() + (sched.window - 1) * 60 * 1000);

      return {
        id: `${order.id}-${this.getDoseName(sched.hour)}`,
        medId: order.medId,
        medName: order.medName,
        route: order.route,
        quantityGiven: order.quantityGiven,
        quantity: order.quantity,
        startDate,
        endDate,
        timeToAdminister: scheduleTimes[idx],
        urgency: this.orderTypeToUrgency(order),
        schedIndex: idx,
        providerOrder: order,
        bloodSugar: '',   // set when administered
        doseGiven: '',    // set when administered
      }
    });
  }

  // Function to handle event click
  handleEventClick(clickedOrder: MedicationScheduleOrder) {
    // Retrieve the order from the Providers orders array.
    // The clickedOrder.id has a suffix added to it -- -morning or -midday or -evening or -night.
    // Remove that before looking for the order.
    const trimmedOrder = clickedOrder.id.replace(/-(morning|midday|evening|night)/, '');
    const order = this.orders.find((o: ProviderOrder) => o.id === trimmedOrder);
    if (order) {
      // pass in which of the n administrations of the medicine was clicked on.
      this.openAdministerMedDialog(order, clickedOrder);
    } else {
      this.toastService.clear();
      this.toastService.addOne({
        severity: ERROR,
        summary: 'Error',
        detail: `Unable to open order ${trimmedOrder}!`,
      });
    }
  }

  // Background of event is gray if the medication has been administered.
  // Otherwise, it is blue for low, yellow for medium, and red for high urgency.
  computeBackgroundColor(order: MedicationScheduleOrder) {
    const providerOrder = order.providerOrder;
    if (providerOrder.whichGiven[order.schedIndex]) {
      return 'gray';
    }
    return order.urgency === 'high' ? 'red' : order.urgency === 'medium' ? 'yellow' : '#295bac';
  }

  computeHtml(medSchedOrder: MedicationScheduleOrder) {
    const dosageStr =
      (medSchedOrder.providerOrder.isSlidingScale || medSchedOrder.providerOrder.isMealTimes) &&
        (medSchedOrder.doseGiven || medSchedOrder.bloodSugar) ?
        `<div>Dose: ${medSchedOrder.doseGiven} Blood sugar: ${medSchedOrder.bloodSugar} </div>` : '';
    return `
        <div class="medication-name" >
          <strong>${medSchedOrder.medName} </strong>
        </div>
        <div class="time-range">
          ${medSchedOrder.timeToAdminister}
        </div>
        ${dosageStr}
    `;
  }

  openAdministerMedDialog(order: ProviderOrder, medOrder: MedicationScheduleOrder): void {
    const dialogRef = this.dialogService.open(AdministerMedDialogComponent, {
      header: `Administering medication for: ${this.patient.getNameFirstMiddleLast()}`,
      data: { patient: this.patient, order },
      width: '50%',
      closable: false,
    });

    // This data comes back from onCloseClick in administer-med-dialog.component.html
    dialogRef.onClose.subscribe((res: {
      order: ProviderOrder;
      administered: boolean;
      bloodSugar: string;     // only for insulin-like drug.
      doseGiven: string;      // only for insulin-like drug.
    }) => {
      if (res.order && res.administered) {
        // Note which of the administrations of the medicine was done.
        res.order.whichGiven[medOrder.schedIndex] = true;

        medOrder.bloodSugar = res.bloodSugar;
        medOrder.doseGiven = res.doseGiven;

        // Update the store with any changes to the objects (quantityGiven, etc.)
        const indexToSplice = this.patient.providerOrders.findIndex((o) => o.id == order.id);
        this.patient.providerOrders.splice(indexToSplice, 1, order);
        this.patientStore.setField({
          field: 'providerOrders',
          data: this.patient.providerOrders,
        });

      }
    });
  }
}
